import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IHostLicense,
    ILicenseFields,
    ITenant,
    ITenantLicense,
    LicenseServerErrorCode,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    HostLicenseService,
    ModalService,
    TenantService,
} from '@services';
import { ISuggestValueData } from '@uipath/angular/components/ui-suggest';
import UiValidators from '@validators';

import {
    merge,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';

const emptySuggestData$ = of({
    id: 0,
    text: '...',
    data: null,
});

@Component({
    templateUrl: './tenant-license.modal.html',
    styleUrls: ['./tenant-license.modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [TenantService],
})
export class TenantLicenseModalComponent
    extends UiModalComponent<ITenant>
    implements OnInit {

    public tenant$!: Observable<ISuggestValueData<ITenant>>;
    public tenantLicense$?: Observable<IHostLicense>;
    public license$!: Observable<IHostLicense>;

    public formGroup!: FormGroup;
    public tenantFormGroup!: FormGroup;
    public isSelfLicensed = false;
    public noTenants = false;

    private _tenantSource = new Subject<number>();
    private _fb: FormBuilder = this._injector.get<FormBuilder>(FormBuilder);
    private _hostLicenseService: HostLicenseService = this._injector.get<HostLicenseService>(HostLicenseService);
    private _tenantService: TenantService = this._injector.get<TenantService>(TenantService);
    private _modalService: ModalService = this._injector.get<ModalService>(ModalService);
    private _translateService: TranslateService = this._injector.get<TranslateService>(TranslateService);
    private _appState: AppStateFacade = this._injector.get<AppStateFacade>(AppStateFacade);

    ngOnInit() {
        this.tenantFormGroup = this._fb.group({
            'tenant': [],
        });
        this.formGroup = this._fb.group({
            'attended': [],
            'unattended': [],
            'nonProduction': [],
            'development': [],
            'studioX': [],
        }, {
                validator: (form: FormGroup) => {
                    let sum = 0;

                    Object
                        .keys(form.controls)
                        .forEach(key =>
                            sum += +form.get(key)!.value,
                        );

                    return !!sum ? null : {
                        noLicense: true,
                    };
                },
            });

        this.tenant$ =
            merge(
                of(this.context.data.id),
                this._tenantSource,
            )
                .pipe(
                    map(id => Number(id)),
                    distinctUntilChanged(),
                    switchMap(this._switchToTenant),
                    tap(this._updateTenantLicense),
                );

        this.license$ = this._appState.user.license.value$ as Observable<IHostLicense>;
        this._appState.user.license.fetch();
    }

    public tenantSourceFactory = (searchTerm: string, pageSize: number, skip: number = 0) => {
        return this._tenantService
            .get({
                orderBy: ['Name'],
                top: pageSize,
                skip,
                filter: {
                    Name: {
                        contains: searchTerm,
                    },
                    IsActive: {
                        eq: true,
                    },
                },
            })
            .pipe(
                tap(
                    response => {
                        const newState = !searchTerm.trim() &&
                            !response.total;
                        if (newState !== this.noTenants) {
                            this.noTenants = newState;
                            this._cd.detectChanges();
                        }
                    },
                ),
                map(
                    response => ({
                        total: response.total,
                        data: response
                            .data
                            .map(this._mapTenant),
                    }),
                ),
            );
    }

    public setLicense(hostLicenseId: number, tenantId: number, licenseConfiguration: ITenantLicense) {
        const event = {
            type: ModalResultType.Update,
        };

        licenseConfiguration.hostLicenseId = hostLicenseId;
        licenseConfiguration.tenantId = tenantId;

        Object
            .keys(licenseConfiguration)
            .forEach(keyStr => {
                const key = keyStr as keyof typeof licenseConfiguration;
                licenseConfiguration[key] = +licenseConfiguration[key];
            });

        this._hostLicenseService
            .setLicenseForTenant(licenseConfiguration)
            .pipe(
                tap(() => {
                    if (this.context.additionalData.closeAfterAllocation) {
                        this._dialogRef.close(event);
                    }
                }),
                switchMap(() => this._appState.user.license.fetch()),
                filter(() => !this.context.additionalData.closeAfterAllocation),
                switchMap(() => this._updateTenantLicense({
                    id: tenantId,
                })),
                tap(() => this._cd.detectChanges()),
                tap(() => this.customEvent.emit(event)),
            )
            .subscribe();
    }

    public removeLicense(tenantId: number) {
        const initial$ = this.isSelfLicensed ?
            this._appState.user.license.deactivateLicenseOnline(tenantId) :
            of(0);

        this._modalService.onConfirm(
            null,
            () => {
                initial$
                    .pipe(
                        switchMap(() => this._hostLicenseService
                            .deleteLicenseForTenant(tenantId)),
                        switchMap(() => this._appState.user.license.fetch()),
                        switchMap(() => this._updateTenantLicense({
                            id: tenantId,
                        })),
                    )
                    .subscribe(
                        () => {
                            this._cd.detectChanges();
                            this.customEvent.emit({
                                type: ModalResultType.Update,
                            });
                        },
                        (error) => {
                            if (error.status === 503
                                && error.error.errorCode === LicenseServerErrorCode.ServerConnectionFailure) {
                                this.customEvent.emit({
                                    type: ModalResultType.Failed,
                                    additionalData: {
                                        tenantId,
                                    },
                                });
                            }
                        });
            },
            () => { },
            this._translateService.instant('tenant_license_confirm_remove_license'),
        );
    }

    public shouldChangeTenant = (tenant?: ITenant) => {
        const isTenantSet = tenant != null &&
            !!tenant.id;
        return !isTenantSet;
    }

    public changeTenant = (entry: ISuggestValueData<ITenant>) => {
        if (!!entry.data) {
            this._tenantSource.next(entry.data.id);
        }
    }

    public getUsedLicenses(used: number, allowed: number, filled: string) {
        const input = parseInt(filled, 10) || 0;
        return used - allowed + input;
    }

    public positiveClamp(number: number) {
        return number < 0 ?
            0 :
            number;
    }

    private _updateFormValues = (tenantLicense: IHostLicense) => {
        Object
            .keys(tenantLicense.allowed)
            .forEach(keyStr => {
                const key = keyStr as keyof typeof tenantLicense.allowed;
                this.formGroup.get(key)!.patchValue(tenantLicense.allowed[key]);
            });
    }

    private _updateFormValidators(tenantLicense: IHostLicense) {
        const controlKeys = Object.keys(this.formGroup.controls) as (keyof ILicenseFields)[];
        for (const controlKey of controlKeys) {
            const control = this.formGroup.get(controlKey);
            const allowed = this._appState.user.license.value!.allowed[controlKey];
            const used = this._appState.user.license.value!.used[controlKey];
            const max = allowed - used;

            const assignedAlready = tenantLicense.allowed[controlKey];

            control!.setValidators(UiValidators.range(0, max + assignedAlready));
        }
    }

    private _updateReadState() {
        if (this.isSelfLicensed) {
            this.formGroup.disable();
        } else {
            this.formGroup.enable();
        }
    }

    private _updateTenantLicense = (tenant: any) => {
        if (!tenant.id) { return of(); }

        return this.tenantLicense$ = this._hostLicenseService
            .getLicenseForTenant(Number(tenant.id))
            .pipe(
                tap(
                    (tenantLicense: IHostLicense) => {
                        this.isSelfLicensed = tenantLicense.isRegistered &&
                            tenantLicense.hostLicenseId == null;
                    },
                ),
                tap(() => this._updateReadState()),
                tap(tenantLicense => this._updateFormValidators(tenantLicense)),
                tap(this._updateFormValues),
            );
    }

    private _switchToTenant = (id: number) => {
        if (!!id) {
            return this._tenantService
                .getInstance(id)
                .pipe(
                    map(this._mapTenant),
                );
        }

        return emptySuggestData$;
    }

    private _mapTenant = (tenant: ITenant) => ({
        id: tenant.id,
        text: tenant.name,
        data: tenant,
    })
}
